import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import {
  Employee_HEADER,
  Employee_MODAL_FIELDS,
} from '../../../constants/Employee';
import {
  EmptyEmployeesGetResponse,
  EmployeesArray,
  EmployeesGetResponse,
} from '../../../types/employee/EmployeeGetResponse';
import {
  createEmployee,
  deleteEmployee,
  getEmployeeList,
  updateEmployee,
} from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderEmployeeRow from './RenderEmployeeRow';
import EditEmployeeRow from './EditEmployeeRow';
import AddEmployeeRow from './AddEmployeeRow';
import SingleAddTable from '../../../components/table/SingleAddTable';
import {
  EmptyEmployeePostRequest,
  EmployeePostRequest,
} from '../../../types/employee/EmployeePostRequest';
import {
  EmptyEmployeePutRequest,
  EmployeePutRequest,
} from '../../../types/employee/EmployeePutRequest';
import TablePagination from '../../../components/table/pagination/TablePagination';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingOverlay from '../../../components/LoadingOverlay';
import {
  DefaultEmployeeGetQueryParams,
  EmployeeGetQueryParams,
} from '../../../types/employee/EmployeeGetQueryParams';
import { SortOrder } from '../../../types/sort/sort';
import {
  EMPLOYEE_SORT_ORDER,
  EMPLOYEE_DEFAULT_SORT_COLUMN,
} from '../../../constants/sort';
import useValidation from '../../../hooks/useValidation';
import { EMPLOYEE_VALIDATION } from '../../../constants/validation/employeeValidation';
import NameSearch from '../../../components/search/NameSearch';
import TokyoCheckbox from '../../../components/search/TokyoCheckbox';
import DairenCheckbox from '../../../components/search/DairenCheckbox';
import UpdateModalSave from '../../../components/modal/UpdateModalSave';
import DeleteModal from '../../../components/modal/DeleteModal';
import { EmptyEmployeeDeleteItem } from '../../../types/employee/EmployeeDeleteItem';
import AddButton from '../../../components/button/AddButton';
import SearchResetButtons from '../../../components/button/SearchResetButtons';
import { employeeCodeComparator } from '../../../utils/employeeCodeComparator';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/EmployeeHeader.css';
import { createNoSubstitutionTemplateLiteral } from 'typescript';

const Employee = () => {
  const [editingId, setEditingId] = useState<number>(-1);
  const [isAdding, setIsAdding] = useState(false);
  const [addingItem, setAddingItem] = useState<EmployeePostRequest>(
    EmptyEmployeePostRequest
  );
  const [editingItem, setEditingItem] = useState<EmployeePutRequest>(
    EmptyEmployeePutRequest
  );
  const [deletingItem, setDeletingItem] = useState<EmployeesArray>(
    EmptyEmployeeDeleteItem
  );
  const [getQueryParams, setGetQueryParams] = useState<EmployeeGetQueryParams>(
    DefaultEmployeeGetQueryParams
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(EMPLOYEE_SORT_ORDER);
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    EMPLOYEE_DEFAULT_SORT_COLUMN
  );
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [form, setForm] = useState({
    employeeId: 0,
    isTokyo: true,
    isDairen: false,
  });
  const [resetSignal, setResetSignal] = useState(0); // 検索リセット用のシグナル

  // 表（テーブル）の横スクロール要素（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  // バリデーション関数。エラー時はエラーメッセージを返す。
  const { validate } = useValidation(EMPLOYEE_VALIDATION);

  const queryClient = useQueryClient();

  // プロジェクト検索のクエリパラメーターをセット
  const setSearchQueryParams = (id: number) => {
    setForm((prev) => ({
      ...prev,
      employeeId: id,
    }));
  };

  // post, put delete成功時の処理
  const handleSubmitSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['EmployeeList', getQueryParams],
    });
    setIsAdding(false);
    setAddingItem(EmptyEmployeePostRequest);
    setEditingId(-1);
  };

  // 一覧取得
  const {
    data: EmployeeGetResponse,
    isLoading,
    isError,
    error,
  } = useQuery<EmployeesGetResponse>({
    queryKey: ['EmployeeList', getQueryParams],
    queryFn: () => getEmployeeList(getQueryParams),
    initialData: EmptyEmployeesGetResponse,
  });

  // 一覧取得失敗時のアラート
  useEffect(() => {
    if (isError) {
      alert(error as any);
    }
  }, [isError]);

  // 追加
  const postRequest = useMutation({
    mutationFn: (data: EmployeePostRequest) => createEmployee(data),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  // 削除
  const deleteRequest = useMutation({
    mutationFn: (id: number) => deleteEmployee(id),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  // 編集
  const editRequest = useMutation({
    mutationFn: (data: EmployeePutRequest) => updateEmployee(data.id, data),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  const onAddButtonClick = () => {
    setIsAdding(true);
    setEditingId(-1);
    setAddingItem(EmptyEmployeePostRequest);
  };

  const onEditButtonClick = (item: EmployeesArray) => {
    setIsAdding(false);
    setAddingItem(EmptyEmployeePostRequest);
    setEditingId(item.id);
    setEditingItem({
      ...item,
    });
  };

  const onDeleteButtonClick = (item: EmployeesArray) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const onEditCancelButtonClick = () => {
    setEditingId(-1);
  };

  const onAddSubmitButtonClick = async () => {
    const validationErrors = validate(addingItem);
    if (validationErrors) {
      alert(validationErrors);
      return;
    }
    postRequest.mutate(addingItem);
  };

  const onEditSubmitButtonClick = async () => {
    setShowUpdateModal(true);
  };

  const onEditModalSubmitButtonClick = async () => {
    const validationErrors = validate(editingItem);
    if (validationErrors) {
      alert(validationErrors);
      return;
    }
    editRequest.mutate({ ...editingItem });
  };

  const onSubmitCancelButtonClick = () => {
    setIsAdding(false);
    setAddingItem(EmptyEmployeePostRequest);
  };

  const stripSpaces = (s: string) => (s ?? '').replace(/[\s\u3000]+/g, '');

  const handleAddItemChange = (
    key: keyof EmployeePostRequest,
    value: string | boolean
  ) => {
    const nextValue =
      key === 'name' && typeof value === 'string' ? stripSpaces(value) : value;

    setAddingItem((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  const handleEditItemChange = (
    key: keyof EmployeePutRequest,
    value: string | boolean
  ) => {
    const nextValue =
      key === 'name' && typeof value === 'string' ? stripSpaces(value) : value;

    setEditingItem((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  // ソートの状態を管理
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートの状態が変わったときにクエリパラメータを更新
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  // post, put, deleteが実行中かどうかをチェック
  const isMutating = [postRequest, deleteRequest, editRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer title="社員一覧">
      <LoadingOverlay isLoading={isLoading} isMutating={isMutating} />

      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-employee gx-0">
          {/* 左：検索条件（幅は固定で縮めない） */}
          <Col className="header-fields-employee order-1">
            <NameSearch
              sortComparator={(a, b) => employeeCodeComparator(a, b)}
              setSelectedId={setSearchQueryParams}
              resetSignal={resetSignal}
            />
            <TokyoCheckbox
              className="ms-3"
              checked={form.isTokyo}
              onChange={(checked) =>
                setForm((prev) => ({ ...prev, isTokyo: checked }))
              }
            />
            <DairenCheckbox
              className="ms-3"
              checked={form.isDairen}
              onChange={(checked) =>
                setForm((prev) => ({ ...prev, isDairen: checked }))
              }
            />
          </Col>

          {/* 右：ボタン群（検索/リセットは常に横並び、その他ボタンは折り返し対象） */}
          <Col xs="auto" className="header-right-employee order-2">
            <div className="actions-group-employee">
              <div className="actions-searchreset-employee">
                <SearchResetButtons
                  onSearch={() => {
                    setGetQueryParams((prev) => ({
                      ...prev,
                      page: 1,
                      employeeId: form.employeeId,
                      isTokyo: form.isTokyo,
                      isDairen: form.isDairen,
                    }));
                  }}
                  onReset={() => {
                    setForm({ employeeId: 0, isTokyo: true, isDairen: false });
                    setGetQueryParams({ ...DefaultEmployeeGetQueryParams });
                    setResetSignal((s) => s + 1);
                  }}
                />
              </div>
              <div className="actions-extra-employee">
                <AddButton onClick={onAddButtonClick} buttonLabel="社員" />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <SingleAddTable<EmployeesArray>
        headers={Employee_HEADER}
        items={EmployeeGetResponse.items}
        actionable
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
        renderItem={(item) => (
          <RenderEmployeeRow
            item={item}
            onEditButtonClick={(i) => onEditButtonClick(i)}
            onDeleteButtonClick={onDeleteButtonClick}
            editingId={editingId}
          />
        )}
        editItem={() => (
          <EditEmployeeRow
            item={editingItem}
            onChange={handleEditItemChange}
            onEditSubmitButtonClick={onEditSubmitButtonClick}
            onEditCancelButtonClick={onEditCancelButtonClick}
          />
        )}
        addItem={() => (
          <AddEmployeeRow
            item={addingItem}
            onChange={handleAddItemChange}
            onAddSubmitButtonClick={onAddSubmitButtonClick}
            onSubmitCancelButtonClick={onSubmitCancelButtonClick}
          />
        )}
        isAdding={isAdding}
        setIsAdding={setIsAdding}
        editingId={editingId}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
      />

      <TablePagination
        totalPages={EmployeeGetResponse.totalPages}
        onPageChange={(page) =>
          setGetQueryParams((prev) => ({ ...prev, page }))
        }
        ref={footerRef}
      />

      {showUpdateModal && (
        <UpdateModalSave<EmployeePutRequest>
          show={showUpdateModal}
          item={editingItem}
          fields={Employee_MODAL_FIELDS}
          onClose={() => setShowUpdateModal(false)}
          onSave={() => {
            onEditModalSubmitButtonClick();
            setShowUpdateModal(false);
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteModal<EmployeesArray>
          show={showDeleteModal}
          item={deletingItem}
          fields={Employee_MODAL_FIELDS}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            deleteRequest.mutate(deletingItem.id);
            setShowDeleteModal(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default Employee;
